import { canCloseDemonstration } from "./demonstrationClose";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { Tag } from "primereact/tag";
import { toast } from "react-toastify";
import { handleGetRequest } from "../../../service/GetTemplate";
import { endPoints } from "../../../Config";
import {
  createDemonstration,
  getDemonstration,
  getDemonstrations,
  performDemonstrationAction,
  updateDemonstration,
} from "./demonstrationApi";
import {
  ACCESSORY_OPTIONS,
  ACTION_META,
  FEEDBACK_RESULT_OPTIONS,
  FORMULATION_OPTIONS,
  LIMIT_OPTIONS,
  MACHINE_TYPE_OPTIONS,
  OBJECTIVE_OPTIONS,
  PAYMENT_OPTIONS,
  PRIORITY_OPTIONS,
  STATUS_FILTERS,
  STATUS_META,
  WATER_SUPPLY_OPTIONS,
} from "./demonstrationConstants";
import "./machineDemonstrations.css";

const COMMERCIAL_ROLES = new Set(["admin", "superAdmin", "sales", "seller"]);
const TECHNICAL_ROLES = new Set([
  "admin",
  "superAdmin",
  "support",
  "technician",
]);
const FIELD_ROLES = new Set(["admin", "superAdmin", "support", "technician"]);
const DEMO_REQUESTER_EMAILS = new Set([
  "lisbeth.velasquez@coffeevending.com.pa",
  "valentina@coffeevending.com.pa",
]);

const PENDING_STATUSES = [
  "submitted",
  "correction_requested",
  "accepted",
  "preparing",
  "ready_for_commercial_review",
  "commercial_rejected",
  "approved_for_transport",
  "active",
  "pending_pickup",
  "pending_commercial_close",
];

const COMPLETED_STATUSES = ["closed", "cancelled"];

const PENDING_STATUS_FILTERS = STATUS_FILTERS.filter(
  (filter) => filter.label !== "Historial"
);

const COMPLETED_STATUS_FILTERS = [
  { label: "Todas", value: "" },
  { label: "Cerradas", value: "closed" },
  { label: "Canceladas", value: "cancelled" },
];

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const makeDefaultForm = () => {
  const installationAt = addDays(new Date(), 1);
  installationAt.setHours(9, 0, 0, 0);
  const pickupAt = addDays(installationAt, 1);
  pickupAt.setHours(17, 0, 0, 0);
  return {
    priority: "normal",
    objective: "first_tasting",
    objectiveDetails: "",
    client: {
      name: "",
      address: "",
      office: "",
      mapUrl: "",
      contactName: "",
      contactPhone: "",
      contactEmail: "",
    },
    schedule: {
      installationAt,
      startsAt: installationAt,
      pickupAt,
      accessInstructions: "",
      leavePoweredOn: true,
    },
    machineRequest: {
      type: "coffee",
      preferredModel: "",
      waterSupply: "unknown",
      paymentMethods: ["free"],
      hasBase: false,
      coolingFridge: false,
      freshMilk: false,
      specialRequests: "",
    },
    tasting: {
      productDetails: "",
      formulation: "standard",
      formulationDetails: "",
      cupType: "Vaso de cartón",
      limitType: "unlimited",
      limitValue: null,
      machinePrice: 0,
    },
    accessories: [],
    otherAccessories: "",
  };
};

const normalizeId = (value) => String(value?._id || value || "");

const getUserName = (user) => {
  if (!user) return "—";
  const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  return name || user.email || "—";
};

const getOptionLabel = (options, value) =>
  options.find((option) => option.value === value)?.label || value || "—";

const formatDateTime = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return new Intl.DateTimeFormat("es-PA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
};

const getProgressStage = (status) => {
  if (["submitted", "correction_requested"].includes(status)) return 1;
  if (["accepted", "preparing"].includes(status)) return 2;
  if (["ready_for_commercial_review", "commercial_rejected"].includes(status))
    return 3;
  if (["approved_for_transport", "active"].includes(status)) return 4;
  if (status === "pending_pickup") return 5;
  if (["pending_commercial_close", "closed"].includes(status)) return 6;
  return 0;
};

const toEditForm = (item) => ({
  priority: item.priority || "normal",
  objective: item.objective || "first_tasting",
  objectiveDetails: item.objectiveDetails || "",
  client: {
    name: item.client?.name || "",
    address: item.client?.address || "",
    office: item.client?.office || "",
    mapUrl: item.client?.mapUrl || "",
    contactName: item.client?.contactName || "",
    contactPhone: item.client?.contactPhone || "",
    contactEmail: item.client?.contactEmail || "",
  },
  schedule: {
    installationAt: item.schedule?.installationAt
      ? new Date(item.schedule.installationAt)
      : null,
    startsAt: item.schedule?.startsAt ? new Date(item.schedule.startsAt) : null,
    pickupAt: item.schedule?.pickupAt ? new Date(item.schedule.pickupAt) : null,
    accessInstructions: item.schedule?.accessInstructions || "",
    leavePoweredOn: item.schedule?.leavePoweredOn !== false,
  },
  machineRequest: {
    type: item.machineRequest?.type || "coffee",
    preferredModel: item.machineRequest?.preferredModel || "",
    waterSupply: item.machineRequest?.waterSupply || "unknown",
    paymentMethods: item.machineRequest?.paymentMethods || [],
    hasBase: Boolean(item.machineRequest?.hasBase),
    coolingFridge: Boolean(item.machineRequest?.coolingFridge),
    freshMilk: Boolean(item.machineRequest?.freshMilk),
    specialRequests: item.machineRequest?.specialRequests || "",
  },
  tasting: {
    productDetails: item.tasting?.productDetails || "",
    formulation: item.tasting?.formulation || "standard",
    formulationDetails: item.tasting?.formulationDetails || "",
    cupType: item.tasting?.cupType || "",
    limitType: item.tasting?.limitType || "unlimited",
    limitValue: item.tasting?.limitValue || null,
    machinePrice: Number(item.tasting?.machinePrice || 0),
  },
  accessories: item.accessories || [],
  otherAccessories: item.otherAccessories || "",
});

const MachineDemonstrations = forwardRef(
  ({ onOpenMovements, integrated = false, onChanged }, ref) => {
  const dispatch = useDispatch();
  const storedUser = JSON.parse(
    localStorage.getItem("userDetails") ||
      localStorage.getItem("adminUser") ||
      "{}"
  );
  const currentUser = storedUser?.data?.adminUser || {};
  const roleUid = String(currentUser?.role?.uid || "");
  const normalizedEmail = String(currentUser?.email || "")
    .trim()
    .toLowerCase();
  const currentUserId = normalizeId(currentUser);
  const isAdmin = roleUid === "admin" || roleUid === "superAdmin";
  const isNamedRequester = DEMO_REQUESTER_EMAILS.has(normalizedEmail);
  const isCommercial = COMMERCIAL_ROLES.has(roleUid) || isNamedRequester;
  const isTechnical =
    TECHNICAL_ROLES.has(roleUid) && (!isNamedRequester || isAdmin);
  const isField = FIELD_ROLES.has(roleUid) && (!isNamedRequester || isAdmin);

  const [pendingItems, setPendingItems] = useState([]);
  const [completedItems, setCompletedItems] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [completedTotal, setCompletedTotal] = useState(0);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [pendingStatusFilter, setPendingStatusFilter] = useState("");
  const [completedStatusFilter, setCompletedStatusFilter] = useState("");
  const [pendingSearch, setPendingSearch] = useState("");
  const [completedSearch, setCompletedSearch] = useState("");
  const [formVisible, setFormVisible] = useState(false);
  const [form, setForm] = useState(makeDefaultForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [machines, setMachines] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [actionVisible, setActionVisible] = useState(false);
  const [actionName, setActionName] = useState("");
  const [actionNote, setActionNote] = useState("");
  const [actionMachineId, setActionMachineId] = useState("");
  const [actionTechnicianId, setActionTechnicianId] = useState("");
  const [actionSaving, setActionSaving] = useState(false);
  const [feedback, setFeedback] = useState({
    result: "",
    customerComments: "",
    processAdjustments: "",
    nextStep: "",
    followUpAt: null,
  });

  const loadDemonstrations = useCallback(async () => {
    setLoading(true);
    const pendingParams = new URLSearchParams();
    pendingParams.set(
      "status",
      pendingStatusFilter || PENDING_STATUSES.join(",")
    );
    if (pendingSearch.trim())
      pendingParams.set("search", pendingSearch.trim());
    pendingParams.set("limit", "100");

    const completedParams = new URLSearchParams();
    completedParams.set(
      "status",
      completedStatusFilter || COMPLETED_STATUSES.join(",")
    );
    if (completedSearch.trim())
      completedParams.set("search", completedSearch.trim());
    completedParams.set("limit", "100");

    const [pendingResponse, completedResponse] = await Promise.all([
      getDemonstrations(pendingParams.toString(), dispatch),
      getDemonstrations(completedParams.toString(), dispatch),
    ]);
    const pendingData = pendingResponse?.data;
    const completedData = completedResponse?.data;
    setPendingItems(
      Array.isArray(pendingData?.items) ? pendingData.items : []
    );
    setCompletedItems(
      Array.isArray(completedData?.items) ? completedData.items : []
    );
    setPendingTotal(Number(pendingData?.total || 0));
    setCompletedTotal(Number(completedData?.total || 0));
    setSummary(pendingData?.summary || completedData?.summary || {});
    setLoading(false);
  }, [
    completedSearch,
    completedStatusFilter,
    dispatch,
    pendingSearch,
    pendingStatusFilter,
  ]);

  useEffect(() => {
    if (integrated) return undefined;
    const timeout = setTimeout(loadDemonstrations, 250);
    return () => clearTimeout(timeout);
  }, [integrated, loadDemonstrations]);

  useEffect(() => {
    if (!isTechnical) return undefined;
    const loadAssignments = async () => {
      const [machineResponse, technicianResponse] = await Promise.all([
        handleGetRequest(
          `${endPoints.getallMachine}?summary=true`,
          false,
          false,
          dispatch
        ),
        handleGetRequest(endPoints.getTechnicians, false, false, dispatch),
      ]);
      const machineList = Array.isArray(machineResponse?.data)
        ? machineResponse.data
        : [];
      setMachines(
        machineList
          .filter((machine) => machine.inStockInventory === true)
          .map((machine) => ({
            value: normalizeId(machine),
            label: [
              machine.machine || machine.model || "Máquina",
              machine.type || "",
              machine.serialNumber || machine.deviceId || "",
              "En taller",
            ]
              .filter(Boolean)
              .join(" · "),
          }))
      );
      const technicianList = Array.isArray(technicianResponse?.data)
        ? technicianResponse.data
        : [];
      setTechnicians(
        technicianList.map((technician) => ({
          value: normalizeId(technician),
          label: getUserName(technician),
        }))
      );
    };
    loadAssignments();
    return undefined;
  }, [dispatch, isTechnical]);

  const summaryCards = useMemo(
    () => [
      {
        label: "Nuevas",
        value:
          Number(summary.submitted || 0) +
          Number(summary.correction_requested || 0),
        icon: "pi pi-file",
        tone: "blue",
      },
      {
        label: "En preparación",
        value:
          Number(summary.accepted || 0) +
          Number(summary.preparing || 0) +
          Number(summary.commercial_rejected || 0),
        icon: "pi pi-box",
        tone: "amber",
      },
      {
        label: "Por validar",
        value: Number(summary.ready_for_commercial_review || 0),
        icon: "pi pi-search",
        tone: "purple",
      },
      {
        label: "Pendientes de retiro",
        value: Number(summary.pending_pickup || 0),
        icon: "pi pi-truck",
        tone: "red",
      },
    ],
    [summary]
  );

  const updateSection = (section, key, value) => {
    setForm((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [key]: value,
      },
    }));
  };

  const toggleFreshMilk = (checked) => {
    setForm((current) => ({
      ...current,
      machineRequest: {
        ...current.machineRequest,
        freshMilk: checked,
        coolingFridge: checked ? true : current.machineRequest.coolingFridge,
      },
      accessories: checked
        ? [
            ...new Set([
              ...current.accessories,
              "milk_fridge",
              "milk_hose",
              "cleaning_kit",
            ]),
          ]
        : current.accessories,
    }));
  };

  const changeWaterSupply = (value) => {
    setForm((current) => ({
      ...current,
      machineRequest: {
        ...current.machineRequest,
        waterSupply: value,
      },
      accessories:
        value === "bottle"
          ? [...new Set([...current.accessories, "water_bottle", "pump"])]
          : current.accessories,
    }));
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(makeDefaultForm());
    setFormVisible(true);
  };

  const openEdit = (item) => {
    setEditingId(item._id);
    setForm(toEditForm(item));
    setDetailVisible(false);
    setFormVisible(true);
  };

  const validateForm = () => {
    if (
      !form.client.name ||
      !form.client.address ||
      !form.client.contactName ||
      !form.client.contactPhone
    ) {
      toast.warn("Complete el cliente, la dirección y la persona de contacto.");
      return false;
    }
    if (!form.schedule.installationAt || !form.schedule.pickupAt) {
      toast.warn("Indique las fechas de instalación y retiro.");
      return false;
    }
    if (
      new Date(form.schedule.pickupAt) <= new Date(form.schedule.installationAt)
    ) {
      toast.warn("La fecha de retiro debe ser posterior a la instalación.");
      return false;
    }
    if (
      form.machineRequest.freshMilk &&
      (!form.machineRequest.coolingFridge ||
        !form.accessories.includes("milk_hose"))
    ) {
      toast.warn("La leche fresca requiere nevera y manguera de leche.");
      return false;
    }
    if (
      form.machineRequest.waterSupply === "bottle" &&
      (!form.accessories.includes("water_bottle") ||
        !form.accessories.includes("pump"))
    ) {
      toast.warn("El botellón requiere incluir botellón y bomba.");
      return false;
    }
    if (
      form.tasting.formulation === "special" &&
      !form.tasting.formulationDetails.trim()
    ) {
      toast.warn("Describa la formulación especial.");
      return false;
    }
    if (form.tasting.limitType !== "unlimited" && !form.tasting.limitValue) {
      toast.warn("Indique el límite de días o tazas.");
      return false;
    }
    if (
      Number(form.tasting.machinePrice || 0) > 0 &&
      (!form.machineRequest.paymentMethods.length ||
        form.machineRequest.paymentMethods.every((method) => method === "free"))
    ) {
      toast.warn(
        "Seleccione un sistema de pago para una demostración con precio."
      );
      return false;
    }
    return true;
  };

  const saveForm = async () => {
    if (!validateForm() || saving) return;
    setSaving(true);
    if (editingId) {
      const updateResponse = await updateDemonstration(
        editingId,
        form,
        dispatch
      );
      if (updateResponse?.data?.data) {
        toast.success("Solicitud actualizada y enviada nuevamente a revisión.");
        setFormVisible(false);
        if (integrated && typeof onChanged === "function") await onChanged();
        else await loadDemonstrations();
      }
    } else {
      const response = await createDemonstration(form, dispatch);
      if (response?.data?.data) {
        toast.success("Solicitud de demostración enviada.");
        setFormVisible(false);
        if (integrated && typeof onChanged === "function") await onChanged();
        else await loadDemonstrations();
      }
    }
    setSaving(false);
  };

  const detailRequestRef = useRef(0);
  const openDetails = async (row, requestedAction = null) => {
    if (!row?._id) return;
    const request = ++detailRequestRef.current;
    setSelected(row);
    setDetailVisible(true);
    setDetailLoading(true);
    try {
      const response = await getDemonstration(row._id, dispatch);
      if (request !== detailRequestRef.current) return;
      const latest = response?.data;
      if (!latest) return;
      setSelected(latest);
      if (requestedAction === "close") {
        if (canCloseDemonstration(latest, currentUser)) openAction("close", latest);
        else toast.warn("Esta demostración no está lista para el cierre o no tiene permiso para cerrarla.");
      }
    } finally {
      if (request === detailRequestRef.current) setDetailLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    openCreate,
    openDetails,
    openClose: (row) => openDetails(row, "close"),
  }));

  const canManageCommercialItem = (item) => {
    if (!isCommercial) return false;
    if (roleUid !== "seller") return true;
    return normalizeId(item?.createdBy) === currentUserId;
  };

  const availableActions = (item) => {
    if (!item) return [];
    const actions = [];
    if (
      [
        "submitted",
        "correction_requested",
        "accepted",
        "preparing",
        "ready_for_commercial_review",
        "commercial_rejected",
      ].includes(item.status) &&
      canManageCommercialItem(item)
    )
      actions.push("edit");
    if (item.status === "submitted" && isTechnical)
      actions.push("request_correction", "accept");
    if (item.status === "accepted" && isTechnical)
      actions.push("start_preparation");
    if (
      [
        "preparing",
        "commercial_rejected",
        "ready_for_commercial_review",
      ].includes(item.status) &&
      isTechnical
    )
      actions.push("mark_ready");
    if (item.status === "approved_for_transport" && isField)
      actions.push("confirm_installed");
    if (item.status === "active" && (isField || canManageCommercialItem(item)))
      actions.push("request_pickup");
    if (item.status === "pending_pickup" && isField)
      actions.push("confirm_returned");
    if (canCloseDemonstration(item, currentUser)) actions.push("close");
    if (
      ![
        "approved_for_transport",
        "active",
        "pending_pickup",
        "pending_commercial_close",
        "closed",
        "cancelled",
      ].includes(item.status) &&
      canManageCommercialItem(item)
    )
      actions.push("cancel");
    return actions;
  };

  const openAction = (action, item = selected) => {
    if (action === "edit" || action === "resubmit") {
      openEdit(item);
      return;
    }
    setSelected(item);
    setActionName(action);
    setActionNote("");
    setActionMachineId(normalizeId(item?.assignedMachine));
    setActionTechnicianId(normalizeId(item?.assignedTechnician));
    setFeedback({
      result: "",
      customerComments: "",
      processAdjustments: "",
      nextStep: "",
      followUpAt: null,
    });
    setActionVisible(true);
  };

  const submitAction = async () => {
    const meta = ACTION_META[actionName] || {};
    if (meta.requiresNote && !actionNote.trim()) {
      toast.warn("Agregue una explicación.");
      return;
    }
    if (meta.requiresAssignment && (!actionMachineId || !actionTechnicianId)) {
      toast.warn("Seleccione la máquina y el técnico responsable.");
      return;
    }
    if (
      meta.requiresFeedback &&
      (!feedback.result || !feedback.customerComments.trim())
    ) {
      toast.warn("El resultado y el feedback del cliente son obligatorios.");
      return;
    }
    setActionSaving(true);
    const payload = {
      action: actionName,
      note: actionNote,
      assignedMachineId: actionMachineId || undefined,
      assignedTechnicianId: actionTechnicianId || undefined,
      feedback: meta.requiresFeedback ? feedback : undefined,
    };
    const response = await performDemonstrationAction(
      selected._id,
      payload,
      dispatch
    );
    const updated = response?.data?.data;
    if (updated) {
      toast.success("Solicitud actualizada.");
      setSelected(updated);
      setActionVisible(false);
      if (integrated && typeof onChanged === "function") await onChanged();
      else await loadDemonstrations();
    }
    setActionSaving(false);
  };

  const statusBody = (row) => {
    const meta = STATUS_META[row.status] || {
      label: row.status,
      severity: "info",
    };
    return (
      <Tag
        value={meta.label}
        severity={meta.severity === "help" ? "info" : meta.severity}
      />
    );
  };

  const priorityBody = (row) => {
    const severity =
      row.priority === "urgent"
        ? "danger"
        : row.priority === "high"
        ? "warning"
        : "info";
    return (
      <Tag
        value={getOptionLabel(PRIORITY_OPTIONS, row.priority)}
        severity={severity}
      />
    );
  };

  const actionBody = (row) => (
    <Button
      label="Ver"
      icon="pi pi-eye"
      className="p-button-text p-button-sm"
      onClick={() => openDetails(row)}
    />
  );

  const renderDemonstrationTable = (tableItems, emptyMessage) => (
    <DataTable
      value={tableItems}
      dataKey="_id"
      loading={loading}
      paginator
      rows={10}
      rowsPerPageOptions={[10, 20, 50]}
      responsiveLayout="scroll"
      emptyMessage={emptyMessage}
      className="demo-table"
    >
      <Column field="requestNumber" header="N.º solicitud" sortable />
      <Column field="client.name" header="Cliente" sortable />
      <Column
        header="Fecha demo"
        body={(row) => formatDateTime(row.schedule?.installationAt)}
        sortable
        sortField="schedule.installationAt"
      />
      <Column
        header="Máquina"
        body={(row) =>
          row.assignedMachine?.machine ||
          row.assignedMachine?.model ||
          getOptionLabel(MACHINE_TYPE_OPTIONS, row.machineRequest?.type)
        }
      />
      <Column
        header="Responsable"
        body={(row) =>
          row.assignedTechnician
            ? getUserName(row.assignedTechnician)
            : getUserName(row.createdBy)
        }
      />
      <Column header="Prioridad" body={priorityBody} />
      <Column
        header="Estado"
        body={statusBody}
        sortable
        sortField="status"
      />
      <Column header="Acciones" body={actionBody} exportable={false} />
    </DataTable>
  );

  const renderForm = () => (
    <div className="demo-form">
      <section className="demo-form-section">
        <h4>1. Cliente y objetivo</h4>
        <div className="grid p-fluid">
          <div className="col-12 md:col-6">
            <label>Cliente o empresa *</label>
            <InputText
              value={form.client.name}
              onChange={(event) =>
                updateSection("client", "name", event.target.value)
              }
            />
          </div>
          <div className="col-12 md:col-3">
            <label>Prioridad *</label>
            <Dropdown
              value={form.priority}
              options={PRIORITY_OPTIONS}
              onChange={(event) =>
                setForm((current) => ({ ...current, priority: event.value }))
              }
            />
          </div>
          <div className="col-12 md:col-3">
            <label>Objetivo *</label>
            <Dropdown
              value={form.objective}
              options={OBJECTIVE_OPTIONS}
              onChange={(event) =>
                setForm((current) => ({ ...current, objective: event.value }))
              }
            />
          </div>
          <div className="col-12">
            <label>Detalles del objetivo</label>
            <InputTextarea
              rows={2}
              value={form.objectiveDetails}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  objectiveDetails: event.target.value,
                }))
              }
            />
          </div>
          <div className="col-12 md:col-8">
            <label>Dirección completa *</label>
            <InputText
              value={form.client.address}
              onChange={(event) =>
                updateSection("client", "address", event.target.value)
              }
            />
          </div>
          <div className="col-12 md:col-4">
            <label>Oficina, piso o salón</label>
            <InputText
              value={form.client.office}
              onChange={(event) =>
                updateSection("client", "office", event.target.value)
              }
            />
          </div>
          <div className="col-12">
            <label>Enlace de Google Maps</label>
            <InputText
              value={form.client.mapUrl}
              onChange={(event) =>
                updateSection("client", "mapUrl", event.target.value)
              }
            />
          </div>
          <div className="col-12 md:col-4">
            <label>Persona de contacto *</label>
            <InputText
              value={form.client.contactName}
              onChange={(event) =>
                updateSection("client", "contactName", event.target.value)
              }
            />
          </div>
          <div className="col-12 md:col-4">
            <label>Teléfono *</label>
            <InputText
              value={form.client.contactPhone}
              onChange={(event) =>
                updateSection("client", "contactPhone", event.target.value)
              }
            />
          </div>
          <div className="col-12 md:col-4">
            <label>Correo</label>
            <InputText
              value={form.client.contactEmail}
              onChange={(event) =>
                updateSection("client", "contactEmail", event.target.value)
              }
            />
          </div>
        </div>
      </section>

      <Divider />

      <section className="demo-form-section">
        <h4>2. Instalación y retiro</h4>
        <div className="grid p-fluid">
          <div className="col-12 md:col-4">
            <label>Fecha y hora de instalación *</label>
            <Calendar
              value={form.schedule.installationAt}
              onChange={(event) =>
                updateSection("schedule", "installationAt", event.value)
              }
              showTime
              hourFormat="12"
              showIcon
            />
          </div>
          <div className="col-12 md:col-4">
            <label>Inicio de la demostración</label>
            <Calendar
              value={form.schedule.startsAt}
              onChange={(event) =>
                updateSection("schedule", "startsAt", event.value)
              }
              showTime
              hourFormat="12"
              showIcon
            />
          </div>
          <div className="col-12 md:col-4">
            <label>Fecha y hora de retiro *</label>
            <Calendar
              value={form.schedule.pickupAt}
              onChange={(event) =>
                updateSection("schedule", "pickupAt", event.value)
              }
              showTime
              hourFormat="12"
              showIcon
            />
          </div>
          <div className="col-12">
            <label>Instrucciones de acceso, instalación y retiro</label>
            <InputTextarea
              rows={3}
              value={form.schedule.accessInstructions}
              onChange={(event) =>
                updateSection(
                  "schedule",
                  "accessInstructions",
                  event.target.value
                )
              }
            />
          </div>
          <div className="col-12">
            <div className="flex align-items-center gap-2">
              <Checkbox
                inputId="leave-powered-on"
                checked={form.schedule.leavePoweredOn}
                onChange={(event) =>
                  updateSection("schedule", "leavePoweredOn", event.checked)
                }
              />
              <label htmlFor="leave-powered-on" className="mb-0">
                Dejar la máquina encendida después de instalar
              </label>
            </div>
          </div>
        </div>
      </section>

      <Divider />

      <section className="demo-form-section">
        <h4>3. Máquina y configuración</h4>
        <div className="grid p-fluid">
          <div className="col-12 md:col-4">
            <label>Tipo de máquina *</label>
            <Dropdown
              value={form.machineRequest.type}
              options={MACHINE_TYPE_OPTIONS}
              onChange={(event) =>
                updateSection("machineRequest", "type", event.value)
              }
            />
          </div>
          <div className="col-12 md:col-4">
            <label>Modelo preferido</label>
            <InputText
              value={form.machineRequest.preferredModel}
              onChange={(event) =>
                updateSection(
                  "machineRequest",
                  "preferredModel",
                  event.target.value
                )
              }
            />
          </div>
          <div className="col-12 md:col-4">
            <label>Fuente de agua *</label>
            <Dropdown
              value={form.machineRequest.waterSupply}
              options={WATER_SUPPLY_OPTIONS}
              onChange={(event) => changeWaterSupply(event.value)}
            />
          </div>
          <div className="col-12 md:col-6">
            <label>Sistemas de pago</label>
            <MultiSelect
              value={form.machineRequest.paymentMethods}
              options={PAYMENT_OPTIONS}
              onChange={(event) =>
                updateSection("machineRequest", "paymentMethods", event.value)
              }
              display="chip"
            />
          </div>
          <div className="col-12 md:col-6 demo-checkbox-grid">
            <div className="flex align-items-center gap-2">
              <Checkbox
                inputId="has-base"
                checked={form.machineRequest.hasBase}
                onChange={(event) =>
                  updateSection("machineRequest", "hasBase", event.checked)
                }
              />
              <label htmlFor="has-base" className="mb-0">
                Con base
              </label>
            </div>
            <div className="flex align-items-center gap-2">
              <Checkbox
                inputId="cooling-fridge"
                checked={form.machineRequest.coolingFridge}
                onChange={(event) =>
                  updateSection(
                    "machineRequest",
                    "coolingFridge",
                    event.checked
                  )
                }
              />
              <label htmlFor="cooling-fridge" className="mb-0">
                Nevera enfriadora
              </label>
            </div>
            <div className="flex align-items-center gap-2">
              <Checkbox
                inputId="fresh-milk"
                checked={form.machineRequest.freshMilk}
                onChange={(event) => toggleFreshMilk(event.checked)}
              />
              <label htmlFor="fresh-milk" className="mb-0">
                Utiliza leche fresca
              </label>
            </div>
          </div>
          <div className="col-12">
            <label>Solicitudes especiales</label>
            <InputTextarea
              rows={3}
              value={form.machineRequest.specialRequests}
              onChange={(event) =>
                updateSection(
                  "machineRequest",
                  "specialRequests",
                  event.target.value
                )
              }
            />
          </div>
        </div>
      </section>

      <Divider />

      <section className="demo-form-section">
        <h4>4. Productos y degustación</h4>
        <div className="grid p-fluid">
          <div className="col-12">
            <label>Productos o bebidas que se degustarán</label>
            <InputTextarea
              rows={3}
              value={form.tasting.productDetails}
              onChange={(event) =>
                updateSection("tasting", "productDetails", event.target.value)
              }
              placeholder="Incluya productos, marcas, bebidas y cantidades estimadas."
            />
          </div>
          <div className="col-12 md:col-4">
            <label>Formulación</label>
            <Dropdown
              value={form.tasting.formulation}
              options={FORMULATION_OPTIONS}
              onChange={(event) =>
                updateSection("tasting", "formulation", event.value)
              }
            />
          </div>
          <div className="col-12 md:col-4">
            <label>Tipo de vaso</label>
            <InputText
              value={form.tasting.cupType}
              onChange={(event) =>
                updateSection("tasting", "cupType", event.target.value)
              }
            />
          </div>
          <div className="col-12 md:col-4">
            <label>Precio en máquina</label>
            <InputNumber
              value={form.tasting.machinePrice}
              onValueChange={(event) =>
                updateSection("tasting", "machinePrice", event.value || 0)
              }
              mode="currency"
              currency="USD"
              locale="en-US"
              min={0}
            />
          </div>
          {form.tasting.formulation === "special" ? (
            <div className="col-12">
              <label>Detalle de la formulación especial *</label>
              <InputTextarea
                rows={2}
                value={form.tasting.formulationDetails}
                onChange={(event) =>
                  updateSection(
                    "tasting",
                    "formulationDetails",
                    event.target.value
                  )
                }
              />
            </div>
          ) : null}
          <div className="col-12 md:col-6">
            <label>Límite de la degustación</label>
            <Dropdown
              value={form.tasting.limitType}
              options={LIMIT_OPTIONS}
              onChange={(event) =>
                updateSection("tasting", "limitType", event.value)
              }
            />
          </div>
          {form.tasting.limitType !== "unlimited" ? (
            <div className="col-12 md:col-6">
              <label>
                {form.tasting.limitType === "days"
                  ? "Cantidad de días *"
                  : "Cantidad de tazas/productos *"}
              </label>
              <InputNumber
                value={form.tasting.limitValue}
                onValueChange={(event) =>
                  updateSection("tasting", "limitValue", event.value)
                }
                min={1}
              />
            </div>
          ) : null}
        </div>
      </section>

      <Divider />

      <section className="demo-form-section">
        <h4>5. Accesorios y logística</h4>
        <div className="grid p-fluid">
          <div className="col-12">
            <label>Accesorios que deben llevar</label>
            <MultiSelect
              value={form.accessories}
              options={ACCESSORY_OPTIONS}
              onChange={(event) =>
                setForm((current) => ({ ...current, accessories: event.value }))
              }
              display="chip"
              filter
            />
            {form.machineRequest.freshMilk ? (
              <small className="demo-rule-note">
                Leche fresca: se añadieron automáticamente nevera, manguera y
                kit de limpieza.
              </small>
            ) : null}
            {form.machineRequest.waterSupply === "bottle" ? (
              <small className="demo-rule-note">
                Botellón: se añadieron automáticamente botellón y bomba.
              </small>
            ) : null}
          </div>
          <div className="col-12">
            <label>Otros accesorios o instrucciones</label>
            <InputTextarea
              rows={2}
              value={form.otherAccessories}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  otherAccessories: event.target.value,
                }))
              }
            />
          </div>
        </div>
      </section>
    </div>
  );

  const formFooter = (
    <div>
      <Button
        label="Cancelar"
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => setFormVisible(false)}
        disabled={saving}
      />
      <Button
        label={editingId ? "Guardar y reenviar" : "Enviar a Técnicos"}
        icon="pi pi-send"
        onClick={saveForm}
        loading={saving}
      />
    </div>
  );

  const renderDetail = () => {
    if (!selected) return null;
    const status = STATUS_META[selected.status] || {
      label: selected.status,
      severity: "info",
    };
    const progressStage = getProgressStage(selected.status);
    const actions = availableActions(selected);
    return (
      <div className="demo-detail">
        <div className="demo-detail-heading">
          <div>
            <span className="demo-request-number">
              {selected.requestNumber}
            </span>
            <h3>{selected.client?.name}</h3>
          </div>
          <Tag
            value={status.label}
            severity={status.severity === "help" ? "info" : status.severity}
          />
        </div>

        {selected.correctionNote ? (
          <div className="demo-alert demo-alert-danger">
            <strong>Corrección solicitada:</strong> {selected.correctionNote}
          </div>
        ) : null}
        {selected.qualityReview?.status === "rejected" ? (
          <div className="demo-alert demo-alert-danger">
            <strong>Validación comercial rechazada:</strong>{" "}
            {selected.qualityReview.note}
          </div>
        ) : null}

        <div className="demo-progress">
          {[
            "Solicitud",
            "Revisión",
            "Preparación",
            "Validación",
            "Instalación",
            "Retiro",
            "Cierre",
          ].map((label, index) => (
            <div
              className={`demo-progress-step ${
                index <= progressStage ? "is-complete" : ""
              }`}
              key={label}
            >
              <span>{index + 1}</span>
              <small>{label}</small>
            </div>
          ))}
        </div>

        <div className="grid">
          <div className="col-12 lg:col-6">
            <Card title="Solicitud comercial" className="h-full">
              <dl className="demo-definition-list">
                <dt>Objetivo</dt>
                <dd>{getOptionLabel(OBJECTIVE_OPTIONS, selected.objective)}</dd>
                <dt>Prioridad</dt>
                <dd>{getOptionLabel(PRIORITY_OPTIONS, selected.priority)}</dd>
                <dt>Contacto</dt>
                <dd>
                  {selected.client?.contactName} ·{" "}
                  {selected.client?.contactPhone}
                </dd>
                <dt>Dirección</dt>
                <dd>
                  {selected.client?.address}
                  {selected.client?.office
                    ? ` · ${selected.client.office}`
                    : ""}
                </dd>
                <dt>Instalación</dt>
                <dd>{formatDateTime(selected.schedule?.installationAt)}</dd>
                <dt>Retiro</dt>
                <dd>{formatDateTime(selected.schedule?.pickupAt)}</dd>
                <dt>Creada por</dt>
                <dd>{getUserName(selected.createdBy)}</dd>
              </dl>
            </Card>
          </div>
          <div className="col-12 lg:col-6">
            <Card title="Máquina y preparación" className="h-full">
              <dl className="demo-definition-list">
                <dt>Tipo solicitado</dt>
                <dd>
                  {getOptionLabel(
                    MACHINE_TYPE_OPTIONS,
                    selected.machineRequest?.type
                  )}
                </dd>
                <dt>Máquina asignada</dt>
                <dd>
                  {selected.assignedMachine
                    ? [
                        selected.assignedMachine.machine ||
                          selected.assignedMachine.model,
                        selected.assignedMachine.serialNumber ||
                          selected.assignedMachine.deviceId,
                      ]
                        .filter(Boolean)
                        .join(" · ")
                    : "Pendiente"}
                </dd>
                <dt>Técnico</dt>
                <dd>{getUserName(selected.assignedTechnician)}</dd>
                <dt>Agua</dt>
                <dd>
                  {getOptionLabel(
                    WATER_SUPPLY_OPTIONS,
                    selected.machineRequest?.waterSupply
                  )}
                </dd>
                <dt>Nevera</dt>
                <dd>{selected.machineRequest?.coolingFridge ? "Sí" : "No"}</dd>
                <dt>Pago</dt>
                <dd>
                  {(selected.machineRequest?.paymentMethods || [])
                    .map((value) => getOptionLabel(PAYMENT_OPTIONS, value))
                    .join(", ") || "—"}
                </dd>
                <dt>Pedido listo por</dt>
                <dd>{getUserName(selected.readyBy)}</dd>
              </dl>
            </Card>
          </div>
          <div className="col-12 lg:col-6">
            <Card title="Degustación">
              <dl className="demo-definition-list">
                <dt>Productos</dt>
                <dd>{selected.tasting?.productDetails || "—"}</dd>
                <dt>Formulación</dt>
                <dd>
                  {getOptionLabel(
                    FORMULATION_OPTIONS,
                    selected.tasting?.formulation
                  )}
                </dd>
                <dt>Vaso</dt>
                <dd>{selected.tasting?.cupType || "—"}</dd>
                <dt>Límite</dt>
                <dd>
                  {getOptionLabel(LIMIT_OPTIONS, selected.tasting?.limitType)}
                  {selected.tasting?.limitValue
                    ? `: ${selected.tasting.limitValue}`
                    : ""}
                </dd>
                <dt>Precio</dt>
                <dd>
                  ${Number(selected.tasting?.machinePrice || 0).toFixed(2)}
                </dd>
              </dl>
            </Card>
          </div>
          <div className="col-12 lg:col-6">
            <Card title="Movimientos vinculados">
              <div className="demo-linked-movement">
                <div>
                  <strong>Instalación</strong>
                  <span>
                    {selected.installationMovement
                      ? `${
                          selected.installationMovement.movementType
                        } · Lista ${
                          selected.installationMovement.checklistProgress || 0
                        }%`
                      : "Se crea al aprobar el control de calidad"}
                  </span>
                </div>
                <div>
                  <strong>Retiro</strong>
                  <span>
                    {selected.removalMovement
                      ? `${selected.removalMovement.movementType} · Lista ${
                          selected.removalMovement.checklistProgress || 0
                        }%`
                      : "Se crea al programar el retiro"}
                  </span>
                </div>
              </div>
              {(selected.installationMovement || selected.removalMovement) &&
              typeof onOpenMovements === "function" ? (
                <Button
                  label="Abrir pestaña Movimientos"
                  icon="pi pi-directions"
                  className="p-button-outlined mt-3"
                  onClick={() => {
                    setDetailVisible(false);
                    onOpenMovements();
                  }}
                />
              ) : null}
            </Card>
          </div>
        </div>

        {selected.auditLogs?.length ? (
          <Card title="Historial" className="mt-3">
            <div className="demo-audit-list">
              {[...selected.auditLogs].reverse().map((log, index) => (
                <div key={`${log.event}-${log.at}-${index}`}>
                  <span className="demo-audit-dot" />
                  <div>
                    <strong>
                      {ACTION_META[log.event]?.label ||
                        (log.event === "created_and_submitted"
                          ? "Solicitud creada y enviada"
                          : log.event)}
                    </strong>
                    <small>
                      {getUserName(log.by)} · {formatDateTime(log.at)}
                    </small>
                    {log.note ? <p>{log.note}</p> : null}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ) : null}

        {actions.length ? (
          <div className="demo-detail-actions">
            {actions.map((action) => {
              const meta = ACTION_META[action];
              return (
                <Button
                  key={action}
                  label={meta.label}
                  icon={meta.icon}
                  className={
                    meta.severity === "danger"
                      ? "p-button-danger p-button-outlined"
                      : action === "approve_quality" || action === "close"
                      ? "p-button-success"
                      : "p-button-outlined"
                  }
                  onClick={() => openAction(action)}
                />
              );
            })}
          </div>
        ) : null}
      </div>
    );
  };

  const actionMeta = ACTION_META[actionName] || {};
  const actionFooter = (
    <div>
      <Button
        label="Cancelar"
        className="p-button-text"
        onClick={() => setActionVisible(false)}
        disabled={actionSaving}
      />
      <Button
        label={actionMeta.label || "Confirmar"}
        icon={actionMeta.icon}
        className={actionMeta.severity === "danger" ? "p-button-danger" : ""}
        onClick={submitAction}
        loading={actionSaving}
      />
    </div>
  );

  return (
    <div className="machine-demonstrations">
      {!integrated ? (
        <>
      <div className="demo-page-heading">
        <div>
          <h3>Solicitudes de demostraciones</h3>
          <p>
            Preparación, validación, instalación temporal, retiro y cierre
            comercial.
          </p>
        </div>
        {isCommercial ? (
          <Button
            label="Nueva solicitud"
            icon="pi pi-plus"
            onClick={openCreate}
          />
        ) : null}
      </div>

      <div className="grid demo-summary-grid">
        {summaryCards.map((card) => (
          <div className="col-12 md:col-6 xl:col-3" key={card.label}>
            <Card className={`demo-summary-card demo-tone-${card.tone}`}>
              <i className={card.icon} />
              <div>
                <span>{card.label}</span>
                <strong>{card.value}</strong>
              </div>
            </Card>
          </div>
        ))}
      </div>

      <Card className="demo-list-card demo-list-section">
        <div className="demo-list-title">
          <div>
            <h4>Demostraciones pendientes</h4>
            <p>Solicitudes que todavía requieren alguna acción o seguimiento.</p>
          </div>
          <Tag value={pendingTotal} severity="warning" />
        </div>
        <div className="demo-list-toolbar">
          <div className="demo-filter-list">
            {PENDING_STATUS_FILTERS.map((filter) => (
              <Button
                key={filter.label}
                label={filter.label}
                className={
                  pendingStatusFilter === filter.value
                    ? "p-button-sm"
                    : "p-button-sm p-button-text p-button-secondary"
                }
                onClick={() => setPendingStatusFilter(filter.value)}
              />
            ))}
          </div>
          <span className="p-input-icon-left demo-search">
            <i className="pi pi-search" />
            <InputText
              value={pendingSearch}
              onChange={(event) => setPendingSearch(event.target.value)}
              placeholder="Buscar cliente o solicitud"
            />
          </span>
        </div>
        {renderDemonstrationTable(
          pendingItems,
          "No hay demostraciones pendientes para este filtro."
        )}
      </Card>

      <Card className="demo-list-card demo-list-section">
        <div className="demo-list-title">
          <div>
            <h4>Demostraciones completadas</h4>
            <p>Historial de solicitudes cerradas o canceladas.</p>
          </div>
          <Tag value={completedTotal} severity="success" />
        </div>
        <div className="demo-list-toolbar">
          <div className="demo-filter-list">
            {COMPLETED_STATUS_FILTERS.map((filter) => (
              <Button
                key={filter.label}
                label={filter.label}
                className={
                  completedStatusFilter === filter.value
                    ? "p-button-sm"
                    : "p-button-sm p-button-text p-button-secondary"
                }
                onClick={() => setCompletedStatusFilter(filter.value)}
              />
            ))}
          </div>
          <span className="p-input-icon-left demo-search">
            <i className="pi pi-search" />
            <InputText
              value={completedSearch}
              onChange={(event) => setCompletedSearch(event.target.value)}
              placeholder="Buscar cliente o solicitud"
            />
          </span>
        </div>
        {renderDemonstrationTable(
          completedItems,
          "No hay demostraciones completadas para este filtro."
        )}
      </Card>
        </>
      ) : null}

      <Dialog
        header={
          editingId
            ? "Corregir solicitud de demostración"
            : "Nueva solicitud de demostración"
        }
        visible={formVisible}
        style={{ width: "min(92vw, 1050px)" }}
        modal
        maximizable
        onHide={() => !saving && setFormVisible(false)}
        footer={formFooter}
        className="demo-form-dialog"
      >
        {renderForm()}
      </Dialog>

      <Dialog
        header={
          selected?.requestNumber
            ? `Detalle ${selected.requestNumber}`
            : "Detalle de demostración"
        }
        visible={detailVisible}
        style={{ width: "min(94vw, 1100px)" }}
        modal
        maximizable
        onHide={() => { detailRequestRef.current += 1; setDetailVisible(false); }}
        className="demo-detail-dialog"
      >
        {detailLoading ? (
          <div className="demo-loading">
            <i className="pi pi-spin pi-spinner" /> Cargando...
          </div>
        ) : (
          renderDetail()
        )}
      </Dialog>

      <Dialog
        header={actionMeta.label || "Actualizar solicitud"}
        visible={actionVisible}
        style={{ width: "min(92vw, 560px)" }}
        modal
        onHide={() => !actionSaving && setActionVisible(false)}
        footer={actionFooter}
      >
        {actionMeta.requiresAssignment ? (
          <div className="grid p-fluid">
            <div className="col-12">
              <label>Máquina asignada *</label>
              <Dropdown
                value={actionMachineId}
                options={machines}
                onChange={(event) => setActionMachineId(event.value)}
                filter
                placeholder="Seleccione una máquina disponible en taller"
                emptyMessage="No hay máquinas disponibles en inventario"
                emptyFilterMessage="No hay máquinas que coincidan"
              />
            </div>
            <div className="col-12">
              <label>Técnico responsable *</label>
              <Dropdown
                value={actionTechnicianId}
                options={technicians}
                onChange={(event) => setActionTechnicianId(event.value)}
                filter
                placeholder="Seleccione un técnico"
              />
            </div>
          </div>
        ) : null}
        {actionMeta.requiresFeedback ? (
          <div className="grid p-fluid">
            <div className="col-12">
              <label>Resultado *</label>
              <Dropdown
                value={feedback.result}
                options={FEEDBACK_RESULT_OPTIONS}
                onChange={(event) =>
                  setFeedback((current) => ({
                    ...current,
                    result: event.value,
                  }))
                }
              />
            </div>
            <div className="col-12">
              <label>Feedback del cliente *</label>
              <InputTextarea
                rows={4}
                value={feedback.customerComments}
                onChange={(event) =>
                  setFeedback((current) => ({
                    ...current,
                    customerComments: event.target.value,
                  }))
                }
              />
            </div>
            <div className="col-12">
              <label>Ajustes recomendados al proceso</label>
              <InputTextarea
                rows={3}
                value={feedback.processAdjustments}
                onChange={(event) =>
                  setFeedback((current) => ({
                    ...current,
                    processAdjustments: event.target.value,
                  }))
                }
              />
            </div>
            <div className="col-12">
              <label>Próximo paso</label>
              <InputText
                value={feedback.nextStep}
                onChange={(event) =>
                  setFeedback((current) => ({
                    ...current,
                    nextStep: event.target.value,
                  }))
                }
              />
            </div>
            <div className="col-12">
              <label>Fecha de seguimiento</label>
              <Calendar
                value={feedback.followUpAt}
                onChange={(event) =>
                  setFeedback((current) => ({
                    ...current,
                    followUpAt: event.value,
                  }))
                }
                showIcon
              />
            </div>
          </div>
        ) : null}
        {!actionMeta.requiresFeedback ? (
          <div className="p-fluid">
            <label>
              {actionMeta.requiresNote ? "Explicación *" : "Observaciones"}
            </label>
            <InputTextarea
              rows={4}
              value={actionNote}
              onChange={(event) => setActionNote(event.target.value)}
              placeholder={
                actionName === "approve_quality"
                  ? "Confirme que máquina, productos y accesorios fueron verificados."
                  : ""
              }
            />
          </div>
        ) : null}
        {["confirm_installed", "confirm_returned"].includes(actionName) ? (
          <div className="demo-alert demo-alert-info mt-3">
            Primero debe completar la lista de verificación y cerrar el
            movimiento correspondiente en esta página.
          </div>
        ) : null}
      </Dialog>
    </div>
  );
  }
);

export default MachineDemonstrations;
